import React, { useEffect, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import { Asset, sameMachine } from './Asset';
import { TempAsset } from './Asset';
import { Entity } from '@repo/frontend-types';
import { TwinEntity } from '@repo/backend-types';
import { useSecureModelURL } from '../../secureRemoteFiles';
import { checkModelExists, getCachedModelExistence } from '@repo/ui';

interface Props {
  entity: Entity;
  mapDiv: HTMLDivElement;
  loadingList: Map<string, boolean>;
  modelFileName: string;
  lineage: TwinEntity[];
}

/**
 * Smart Asset component that checks if a model exists before attempting to load it
 * This prevents 404 errors and improves loading performance
 */
const SmartAssetImpl = ({ entity, mapDiv, loadingList, modelFileName, lineage }: Props) => {
  // Resolve filename variants to handle '+', spaces, and underscores
  const [resolvedFileName, setResolvedFileName] = useState(modelFileName);

  useEffect(() => {
    // Load exactly the provided filename; avoid any preflight network checks or transformations
    setResolvedFileName(modelFileName);
  }, [modelFileName]);

  const modelURL = useSecureModelURL(resolvedFileName);
  const [modelAvailable, setModelAvailable] = useState<boolean | null>(() => {
    const cached = modelURL ? getCachedModelExistence(modelURL) : undefined;
    return cached === undefined ? null : cached;
  });

  // Check existence before attempting to load to avoid 404s breaking the scene
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!modelURL) {
        if (!cancelled) setModelAvailable(false);
        return;
      }
      const cached = getCachedModelExistence(modelURL);
      if (cached !== undefined) {
        if (!cancelled) setModelAvailable(cached);
        return;
      }
      const exists = await checkModelExists(modelURL);
      if (!cancelled) setModelAvailable(exists);
    })();
    return () => {
      cancelled = true;
    };
  }, [modelURL]);
  // Preload only when we know the model exists
  useEffect(() => {
    if (modelURL && modelAvailable === true) {
      try {
        useGLTF.preload(modelURL);
      } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelURL, modelAvailable]);

  // Keep loading list in sync so overall scene completion logic doesn't stall
  useEffect(() => {
    if (!loadingList.has(resolvedFileName)) {
      loadingList.set(resolvedFileName, false);
    }
    if (modelAvailable !== null) {
      // Mark as loaded for either real model (true) or placeholder (false)
      loadingList.set(resolvedFileName, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedFileName, modelAvailable]);

  // If still checking for the first time and no cache, avoid flicker
  if (modelAvailable === null) {
    return null;
  }

  // If model is not available, render placeholder so the scene can finish
  if (modelAvailable === false) {
    return <TempAsset entity={entity} mapDiv={mapDiv} lineage={lineage} />;
  }

  return (
    <Asset
      entity={entity}
      mapDiv={mapDiv}
      loadingList={loadingList}
      modelFileName={resolvedFileName}
      lineage={lineage}
    />
  );
};

// The same rule as `Asset`'s: id-only here would stop a moved machine before it reached `Asset`.
export const SmartAsset = React.memo(SmartAssetImpl, sameMachine);
